import cloneDeep from 'lodash.clonedeep';
import { action, computed, observable, makeObservable } from 'mobx';
import {
  defaultConfigWeb,
  defaultConfigWebColors,
  defaultConfigWebColorsValidation,
  defaultConfigWebConfiguration,
  defaultConfigWebContactValidation,
  defaultConfigWebImages,
} from '../constants';
import { configWebDataCast, getConfigWebParams } from '../helpers';
import {
  IConfigWeb,
  IConfigWebColors,
  IConfigWebColorsValidation,
  IConfigWebConfiguration,
  IConfigWebContactValidation,
  IConfigWebImages,
  IResponseConfigWeb,
  TConfigWebColorField,
} from '../types';
import client from 'src/services/client';
import { IValidation, LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { emailValidation, emptyNameValidation, phoneValidation, validationColors } from 'src/utils/validation';
import logger from '~/services/logger';
import { scrollToErrorElement } from '~/utils/helpers';

class ConfigWebStore {
  constructor() {
    makeObservable(this);
  }

  @observable colors: IConfigWebColors = defaultConfigWebColors;
  @observable images: IConfigWebImages = defaultConfigWebImages;
  @observable configuration: IConfigWebConfiguration = defaultConfigWebConfiguration;

  @observable loading: LoadingStatus = LoadingStatus.Init;
  @observable isEdit: boolean = false;

  @observable focusField: TConfigWebColorField | null = null;
  @action setFocusField = (field: TConfigWebColorField | null) => {
    this.focusField = field;
  };

  @computed get isFocusFieldValue(): string {
    if (this.focusField) return this.colors[this.focusField];
    return '';
  }

  @action changeColors = (value: Partial<IConfigWebColors>) => {
    this.colors = { ...this.colors, ...value };
    this.isEdit = true;

    // update validation
    if (!this.colorsValidation.isValid) this.setColorsValidation();
  };

  @action changeImages = (value: Partial<IConfigWebImages>) => {
    this.images = { ...this.images, ...value };
    this.isEdit = true;
  };

  @action changeConfigLanguagePicker = (value: IConfigWebConfiguration['defaultLanguage']) => {
    this.configuration.defaultLanguage = value;
    this.isEdit = true;
  };

  @action changeConfigWebContact = (value: Partial<IConfigWebConfiguration['webContact']>) => {
    this.configuration.webContact = { ...this.configuration.webContact, ...value };
    this.isEdit = true;

    // update validation
    if (!this.contactValidation.isValid) this.setContactValidation();
  };

  @action changeConfigMetaDetails = (value: Partial<IConfigWebConfiguration['metaDetails']>) => {
    this.configuration.metaDetails = { ...this.configuration.metaDetails, ...value };
    this.isEdit = true;
  };

  @action changeIsHumanReadableUrl = (value: TBooleanInNumber) => {
    this.configuration.is_human_readable_url = Boolean(value);
    this.isEdit = true;
  };

  @action changeConfigAppLinks = (value: Partial<IConfigWebConfiguration['appLinks']>) => {
    this.configuration.appLinks = { ...this.configuration.appLinks, ...value };
    this.isEdit = true;
  };

  @action changeConfigSocialLinks = (value: Partial<IConfigWebConfiguration['socialLinks']>) => {
    this.configuration.socialLinks = { ...this.configuration.socialLinks, ...value };
    this.isEdit = true;
  };

  private configWeb: IConfigWeb = defaultConfigWeb;
  @action public setData = (data: IResponseConfigWeb) => {
    this.configWeb = configWebDataCast(data);

    this.colors = this.configWeb.colors;
    this.images = this.configWeb.images;
    this.configuration = this.configWeb.configuration;
  };

  @action submit = () => {
    this.setValidations();
    if (!this.isValid) {
      scrollToErrorElement();
      return;
    }

    this.updateSettings();
  };

  private get params() {
    return getConfigWebParams({ colors: this.colors, images: this.images, configuration: this.configuration }, this.configWeb);
  }

  @action updateSettings = async () => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.put('/v3/settings/consumer-channels/web', this.params);

      this.configWeb = { colors: this.colors, images: this.images, configuration: this.configuration };
      this.isEdit = false;
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'ConfigWebStore updateSettings', showModal: true });
    }
  };

  @computed get isValid(): boolean {
    return this.colorsValidation.isValid && this.contactValidation.isValid;
  }

  @action setValidations = () => {
    this.setColorsValidation();
    this.setContactValidation();
  };

  @observable colorsValidation: IConfigWebColorsValidation = cloneDeep(defaultConfigWebColorsValidation);
  @action setColorsValidation = () => {
    this.colorsValidation = cloneDeep(defaultConfigWebColorsValidation);

    const colorsValidationKeys = Object.keys(this.colorsValidation);
    colorsValidationKeys.shift(); // delete isValid key

    colorsValidationKeys.forEach((key) => {
      let keyValue = this.colorsValidation[key as keyof IConfigWebColorsValidation] as IValidation;

      keyValue = validationColors(this.colors[key as TConfigWebColorField], {
        rgb: false,
        html: false,
        hsla: false,
      });

      // reset error text
      keyValue.errorText = undefined;

      (this.colorsValidation[key as keyof IConfigWebColorsValidation] as IValidation) = keyValue;
      if (!keyValue.isValid) this.colorsValidation.isValid = false;
    });
  };
  @observable contactValidation: IConfigWebContactValidation = cloneDeep(defaultConfigWebContactValidation);
  @action setContactValidation = () => {
    this.contactValidation = cloneDeep(defaultConfigWebContactValidation);

    [this.contactValidation.businessName, this.contactValidation.isValid] = emptyNameValidation(
      this.configuration.webContact.businessName,
      this.contactValidation.businessName,
      this.contactValidation.isValid
    );

    this.contactValidation.email = emailValidation(this.configuration.webContact.email);

    this.contactValidation.mobile = { isValid: phoneValidation(this.configuration.webContact.mobile) };

    // reset error text
    this.contactValidation.businessName.enErrorText = undefined;
    this.contactValidation.businessName.arErrorText = undefined;
    this.contactValidation.email.errorText = undefined;
    this.contactValidation.mobile.errorText = undefined;

    if (!this.contactValidation.email.isValid || !this.contactValidation.mobile.isValid) this.contactValidation.isValid = false;
  };

  @action clearStore = () => {
    this.setFocusField(null);
    this.isEdit = false;

    this.colors = this.configWeb.colors;
    this.images = this.configWeb.images;
    this.configuration = this.configWeb.configuration;

    this.colorsValidation = cloneDeep(defaultConfigWebColorsValidation);
    this.contactValidation = cloneDeep(defaultConfigWebContactValidation);
  };
}

const configWebStore = new ConfigWebStore();

export default configWebStore;
